import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Grid3x3, List, Printer, Crown, Car, Truck, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'all',          label: 'All Vehicles' },
  { key: 'ace_owned',    label: 'ACE Owned' },
  { key: 'driving_rentals', label: 'Driving Rentals' },
  { key: 'sedan',        label: 'Sedans' },
  { key: 'suv',          label: 'SUVs' },
  { key: 'group',        label: 'Group & Events' },
  { key: 'exotic',       label: 'Exotic Collection' },
];

const OWNERSHIP_PILL = {
  ace_owned:        'bg-emerald-100 text-emerald-700 border-emerald-300',
  driving_rentals:  'bg-blue-100 text-blue-700 border-blue-300',
  brokered:         'bg-amber-100 text-amber-700 border-amber-300',
  unknown:          'bg-gray-100 text-gray-600 border-gray-200',
};
const OWNERSHIP_LABEL = {
  ace_owned: 'ACE Owned',
  driving_rentals: 'Driving Rentals',
  brokered: 'Brokered',
  unknown: '—',
};

const FleetRateCard = () => {
  const [tiers, setTiers] = useState({});
  const [fleet, setFleet] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [view, setView] = useState('grid');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [settings, setSettings] = useState({ exotic_no_discount: true });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/command-center/ops/membership-tiers`).then(r => r.json()),
      fetch(`${API}/api/rates`).then(r => r.json()).catch(() => ({ vehicles: [] })),
      fetch(`${API}/api/rentals/fleet`).then(r => r.json()).catch(() => ({ vehicles: [] })),
      fetch(`${API}/api/command-center/ops/settings`).then(r => r.json()),
    ]).then(([t, r, ren, s]) => {
      setTiers(t.tiers || {});
      setFleet(r.vehicles || r.rates || []);
      setRentals(ren.vehicles || []);
      setSettings(s);
    });
  }, []);

  const all = useMemo(() => {
    // Normalize chauffeured fleet (from /api/rates) + self-drive rentals
    const norm = [];
    fleet.forEach(v => {
      norm.push({
        id: v.id || v.vehicle_id || v.name,
        name: v.name || v.vehicle_name || v.vehicle,
        category: (v.category || 'sedan').toLowerCase(),
        retail_rate: v.hourly_rate || v.hourlyRate || v.rate || v.retail_rate || 100,
        rate_unit: '/hr',
        ownership: v.ownership || 'ace_owned',
        capacity: v.capacity || v.passengers || '—',
        min_hrs: v.min_hours || v.minHours || 2,
        best_for: v.best_for || v.bestFor || '',
        image: v.image || v.thumbnail_url || v.gallery?.[0],
        is_exotic: ['exotic', 'sport'].includes((v.category || '').toLowerCase()),
      });
    });
    rentals.forEach(v => {
      norm.push({
        id: v.rental_id,
        name: v.vehicle,
        category: (v.category || 'sedan').toLowerCase() === 'sport' ? 'exotic' : (v.category || 'sedan').toLowerCase(),
        retail_rate: v.retail_rate,
        rate_unit: '/day',
        ownership: 'driving_rentals',
        capacity: v.specs?.seats || '—',
        min_hrs: 1,
        best_for: v.category === 'exotic' ? 'Exotic Self-Drive' : 'Self-Drive Rental',
        image: v.thumbnail_url,
        is_exotic: ['exotic'].includes((v.category || '').toLowerCase()),
      });
    });
    return norm;
  }, [fleet, rentals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(v => {
      if (filter === 'ace_owned' && v.ownership !== 'ace_owned') return false;
      if (filter === 'driving_rentals' && v.ownership !== 'driving_rentals') return false;
      if (filter === 'sedan' && v.category !== 'sedan' && v.category !== 'luxury') return false;
      if (filter === 'suv' && v.category !== 'suv') return false;
      if (filter === 'group' && !['group', 'sprinter', 'shuttle'].includes(v.category)) return false;
      if (filter === 'exotic' && !v.is_exotic) return false;
      if (q && !v.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [all, filter, search]);

  const computeRange = (v) => {
    if (!tiers.starter) return null;
    // Exotic: no discount on any tier per settings policy
    if (v.is_exotic && settings.exotic_no_discount) return { low: v.retail_rate, high: v.retail_rate, locked: true };
    // Apply each tier's discount %
    const rates = ['starter', 'plus', 'premier', 'essentials', 'executive', 'black_card']
      .map(k => tiers[k] && Math.round(v.retail_rate * (1 - (tiers[k].discount_pct || 0) / 100)))
      .filter(Boolean);
    return { low: Math.min(...rates), high: Math.max(...rates), locked: false };
  };

  const handlePrint = () => window.print();

  return (
    <div className="bg-white min-h-screen print:bg-white" data-testid="fleet-rate-card-page">
      {/* HERO */}
      <section className="bg-gradient-to-br from-black via-gray-950 to-black text-white py-14 print:bg-white print:text-black">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 print:text-black">FLEET & RATE CARD · 2025–2026</p>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight" data-testid="fleet-card-h1">Every Vehicle. Every Tier. <em className="not-italic text-red-500">Every Price.</em></h1>
          <p className="text-sm text-gray-400 mt-3 max-w-2xl">All chauffeured + self-drive vehicles in one card. Retail rate plus the member-rate range from Starter to Black Card. <strong>Exotic = retail only — no discounts apply.</strong></p>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="sticky top-16 sm:top-20 bg-white border-b border-gray-200 z-30 print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vehicles…" className="pl-9 h-9 text-xs" data-testid="fleet-search" />
          </div>
          <div className="flex gap-1 overflow-x-auto" data-testid="fleet-filter-chips">
            {CATEGORIES.map(c => (
              <button key={c.key} onClick={() => setFilter(c.key)}
                className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full whitespace-nowrap ${filter === c.key ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`fleet-filter-${c.key}`}>{c.label}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setView('grid')} className={`p-1.5 rounded ${view === 'grid' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="view-grid"><Grid3x3 className="h-3.5 w-3.5" /></button>
            <button onClick={() => setView('list')} className={`p-1.5 rounded ${view === 'list' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="view-list"><List className="h-3.5 w-3.5" /></button>
            <Button size="sm" variant="outline" onClick={handlePrint} className="text-xs h-8" data-testid="fleet-print"><Printer className="h-3 w-3 mr-1" /> Print</Button>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="py-8 max-w-7xl mx-auto px-6">
        <p className="text-xs text-gray-500 mb-4">Showing {filtered.length} of {all.length} vehicles</p>

        {view === 'grid' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 print:grid-cols-2" data-testid="fleet-grid">
            {filtered.map(v => {
              const range = computeRange(v);
              return (
                <Card key={v.id} className="overflow-hidden border-gray-200 hover:border-red-200 transition-all" data-testid={`fleet-card-${v.id}`}>
                  {v.image && (
                    <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                      <img src={v.image} alt={v.name} className="w-full h-full object-cover" loading="lazy" />
                      <span className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded border ${OWNERSHIP_PILL[v.ownership]}`}>{OWNERSHIP_LABEL[v.ownership]}</span>
                      {v.is_exotic && <span className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded bg-red-600 text-white">Retail Only</span>}
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{v.category}</p>
                    <h3 className="text-sm font-black text-black mt-1 truncate">{v.name}</h3>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                      <span>{v.capacity} pax</span><span>·</span><span>{v.min_hrs}h min</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-[10px] uppercase font-bold text-gray-500">Retail</span>
                        <span className="text-lg font-black">${v.retail_rate}<span className="text-[10px] font-normal text-gray-400">{v.rate_unit}</span></span>
                      </div>
                      {range && (
                        <div className="flex items-baseline justify-between">
                          <span className="text-[10px] uppercase font-bold text-red-600">Member</span>
                          <span className="text-sm font-bold text-red-600">
                            {range.locked
                              ? <span className="text-gray-400">— Retail Only —</span>
                              : `$${range.high}–$${range.low}${v.rate_unit}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          /* LIST view */
          <Card className="border-gray-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-left">
                <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="px-3 py-2.5">Vehicle</th>
                  <th className="px-3 py-2.5">Ownership</th>
                  <th className="px-3 py-2.5">Category</th>
                  <th className="px-3 py-2.5 text-right">Capacity</th>
                  <th className="px-3 py-2.5 text-right">Retail</th>
                  <th className="px-3 py-2.5 text-right">Member Range</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(v => {
                  const range = computeRange(v);
                  return (
                    <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`fleet-row-${v.id}`}>
                      <td className="px-3 py-2.5"><p className="font-bold">{v.name}</p><p className="text-[10px] text-gray-500">{v.best_for}</p></td>
                      <td className="px-3 py-2.5"><span className={`inline-block text-[9px] uppercase font-bold px-2 py-0.5 rounded border ${OWNERSHIP_PILL[v.ownership]}`}>{OWNERSHIP_LABEL[v.ownership]}</span></td>
                      <td className="px-3 py-2.5 capitalize">{v.category}</td>
                      <td className="px-3 py-2.5 text-right">{v.capacity}</td>
                      <td className="px-3 py-2.5 text-right font-bold">${v.retail_rate}{v.rate_unit}</td>
                      <td className="px-3 py-2.5 text-right">
                        {range?.locked ? <span className="text-gray-400">Retail Only</span> : range && <span className="font-bold text-red-600">${range.high}–${range.low}{v.rate_unit}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </section>

      {/* Footer / contact */}
      <section className="bg-black text-white py-8 print:bg-white print:text-black">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] text-gray-400">ACE · YourAutoConcierge.com · 1954 Airport Rd Suite 500, Chamblee GA · 2025–2026</p>
          <a href="tel:4045438930" className="inline-flex items-center text-xs font-bold text-red-500 hover:text-red-400"><Phone className="h-3 w-3 mr-1.5" /> (404) 543-8930</a>
        </div>
      </section>
    </div>
  );
};

export default FleetRateCard;
